import apiClient from './client';
import { ApiResponse, Pagination } from '@/types';

/**
 * Message direction
 */
export type MessageDirection = 'incoming' | 'outgoing';

/**
 * Message status
 */
export type MessageStatus = 'pending' | 'sent' | 'delivered' | 'read' | 'failed';

/**
 * Message type
 */
export type MessageType =
  | 'standup_prompt'
  | 'standup_response'
  | 'standup_reminder'
  | 'support_prompt'
  | 'support_response'
  | 'notification'
  | 'alert'
  | 'report'
  | 'general';

/**
 * Message interface
 */
export interface Message {
  _id: string;
  rocketchatMessageId?: string;
  rocketchatRoomId?: string;
  rocketchatUserId?: string;
  rocketchatUsername?: string;
  userId?: {
    _id: string;
    username: string;
    displayName: string;
    email: string;
    role: string;
  } | string;
  direction: MessageDirection;
  type: MessageType;
  content: string;
  status: MessageStatus;
  sentAt?: string;
  deliveredAt?: string;
  readAt?: string;
  failedAt?: string;
  failureReason?: string;
  aiProcessed?: boolean;
  aiProcessedAt?: string;
  aiResponse?: string;
  contextType?: 'standup' | 'support_validation' | 'code_review' | 'alert';
  contextId?: string;
  conversationId?: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Get messages for a user
 */
export async function getUserMessages(userId: string, options?: {
  limit?: number;
  type?: MessageType;
  days?: number;
}): Promise<Message[]> {
  const params = new URLSearchParams();
  if (options?.limit) params.append('limit', options.limit.toString());
  if (options?.type) params.append('type', options.type);
  if (options?.days) params.append('days', options.days.toString());

  const url = `/messages/user/${userId}${params.toString() ? `?${params}` : ''}`;
  const response = await apiClient.get<ApiResponse<Message[]>>(url);
  return response.data.data as Message[];
}

/**
 * Send a message to a user
 */
export async function sendMessage(userId: string, content: string): Promise<Message> {
  const response = await apiClient.post<ApiResponse<Message>>('/messages/send', {
    userId,
    content,
  });
  return response.data.data as Message;
}

/**
 * Get messages with filters
 */
export async function getMessages(options?: {
  userId?: string;
  type?: MessageType;
  direction?: MessageDirection;
  status?: MessageStatus;
  startDate?: string;
  endDate?: string;
  limit?: number;
  offset?: number;
}): Promise<{ data: Message[]; pagination: Pagination }> {
  const params = new URLSearchParams();
  if (options?.userId) params.append('userId', options.userId);
  if (options?.type) params.append('type', options.type);
  if (options?.direction) params.append('direction', options.direction);
  if (options?.status) params.append('status', options.status);
  if (options?.startDate) params.append('startDate', options.startDate);
  if (options?.endDate) params.append('endDate', options.endDate);
  if (options?.limit) params.append('limit', options.limit.toString());
  if (options?.offset) params.append('offset', options.offset.toString());

  const url = `/messages${params.toString() ? `?${params}` : ''}`;
  const response = await apiClient.get<ApiResponse<Message[]>>(url);

  const meta = response.data.meta as { pagination?: { total: number; limit: number; offset: number } };
  const paginationData = meta?.pagination || { total: 0, limit: options?.limit || 50, offset: options?.offset || 0 };
  const limit = paginationData.limit;
  const offset = paginationData.offset;
  const total = paginationData.total;
  const page = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit);

  return {
    data: response.data.data as Message[],
    pagination: {
      page,
      limit,
      total,
      totalPages,
      hasNext: page < totalPages,
      hasPrev: page > 1,
    },
  };
}

/**
 * Get conversation messages
 */
export async function getConversation(conversationId: string): Promise<Message[]> {
  const response = await apiClient.get<ApiResponse<Message[]>>(`/messages/conversation/${conversationId}`);
  return response.data.data as Message[];
}

/**
 * Rocket.Chat message from API
 */
export interface RocketChatMessage {
  _id: string;
  rid: string;
  msg: string;
  ts: string;
  u: {
    _id: string;
    username: string;
    name?: string;
  };
  _updatedAt: string;
}

/**
 * Get direct message history from Rocket.Chat
 */
export async function getRocketChatDMHistory(username: string, count: number = 50): Promise<RocketChatMessage[]> {
  const response = await apiClient.get<ApiResponse<RocketChatMessage[]>>(`/rocketchat/dm/${username}?count=${count}`);
  return response.data.data as RocketChatMessage[];
}
